import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useAgPlan,
  useAgPlanerMap,
  useAgPlanerTerrain,
  useAgPlans,
  useNobleCandidates,
  useSetAgTypeColor,
  useWorldOptions,
} from '../api/queries';
import type { User } from '../api/types';
import { MapCanvas, type MapController } from '../ag-planer/MapCanvas';
import { COLORS, continentOf, formatDateTime, formatPoints, parseCoordinates, NOBLES_PER_TARGET, type ColorMode, type MapArrow, type MapMarker } from '../ag-planer/mapUtils';
import { NoblePlanningPanel, type IncRow } from '../ag-planer/NoblePlanningPanel';
import { PlanPanel } from '../ag-planer/PlanPanel';
import { loadSprites, type Sprites } from '../ag-planer/sprites';
import { Spinner } from '../components/Spinner';

type Highlight = { kind: 'player' | 'tribe'; index: number } | null;

const COLOR_SAVE_DELAY_MS = 600;

function hiddenTypesStorageKey(welt: string, planId: number): string {
  return `twtool-ag-hidden-${welt}-${planId}`;
}

function readHiddenTypes(welt: string, planId: number): Set<string> {
  try {
    const raw = window.localStorage.getItem(hiddenTypesStorageKey(welt, planId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}


const SEARCH_LIMIT = 6;

/**
 * "AG Planer": die Stämme-Karte der Welt als Vollbild-Canvas (Sprites und
 * Gelände aus dem Spiel). Nur Stammesführer/Administratoren, weltenspezifisch.
 * Die rechte Seitenleiste ist der Platz für Kartenoptionen und später für die
 * Planansicht.
 */
export function AgPlanerPage({ user }: { user: User }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const needsWorldChoice = user.world === null;
  const worldKey = searchParams.get('welt') ?? '';
  const worlds = useWorldOptions('login');
  const activeWorld = user.world?.key ?? worldKey;

  const mapQuery = useAgPlanerMap(activeWorld || undefined);
  const map = mapQuery.data ?? null;
  const terrainReady = map?.terrain.status === 'ready';
  const terrainQuery = useAgPlanerTerrain(activeWorld || undefined, terrainReady);

  // Plan: gewählter Plan steht in der URL (Reload/Link behält ihn), Sichtbarkeit je Typ lokal im Browser.
  const plansQuery = useAgPlans(activeWorld || undefined);
  const planIdParam = Number(searchParams.get('plan'));
  const planId = Number.isInteger(planIdParam) && planIdParam > 0 ? planIdParam : null;
  const planQuery = useAgPlan(activeWorld || undefined, planId);
  const planDetail = planId !== null ? (planQuery.data ?? null) : null;
  const setTypeColor = useSetAgTypeColor(activeWorld || undefined);

  const selectPlan = useCallback(
    (id: number | null) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (id === null) next.delete('plan');
          else next.set('plan', String(id));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());
  useEffect(() => {
    setHiddenTypes(planId !== null && activeWorld ? readHiddenTypes(activeWorld, planId) : new Set());
  }, [activeWorld, planId]);

  const updateHiddenTypes = (next: Set<string>) => {
    setHiddenTypes(next);
    if (planId === null || !activeWorld) return;
    try {
      window.localStorage.setItem(hiddenTypesStorageKey(activeWorld, planId), JSON.stringify([...next]));
    } catch {
      // Speicher nicht verfügbar -- die Auswahl gilt dann nur für diese Sitzung.
    }
  };

  const [colorOverrides, setColorOverrides] = useState<Record<string, string>>({});
  const colorTimers = useRef(new Map<string, number>());
  const changeColor = (key: string, color: string) => {
    setColorOverrides((previous) => ({ ...previous, [key]: color }));
    window.clearTimeout(colorTimers.current.get(key));
    colorTimers.current.set(
      key,
      window.setTimeout(() => setTypeColor.mutate({ typeKey: key, color }), COLOR_SAVE_DELAY_MS),
    );
  };
  useEffect(() => {
    const timers = colorTimers.current;
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const effectiveColors = useMemo(() => {
    const colors: Record<string, string> = {};
    for (const type of planDetail?.types ?? []) colors[type.key] = colorOverrides[type.key] ?? type.color;
    return colors;
  }, [planDetail, colorOverrides]);

  const arrows = useMemo<MapArrow[]>(() => {
    if (!planDetail) return [];
    const result: MapArrow[] = [];
    for (const [sx, sy, tx, ty, , typeIndex] of planDetail.commands) {
      const type = planDetail.types[typeIndex];
      if (!type || hiddenTypes.has(type.key)) continue;
      result.push({ sx, sy, tx, ty, color: effectiveColors[type.key] ?? type.color });
    }
    return result;
  }, [planDetail, hiddenTypes, effectiveColors]);

  // AG-Markierungen ein-/ausblenden (Browser-weit gemerkt, standardmäßig an).
  const [showNobles, setShowNobles] = useState(() => {
    try {
      return window.localStorage.getItem('twtool-ag-show-nobles') !== '0';
    } catch {
      return true;
    }
  });
  const toggleNobles = () => {
    const next = !showNobles;
    setShowNobles(next);
    try {
      window.localStorage.setItem('twtool-ag-show-nobles', next ? '1' : '0');
    } catch {
      // Speicher nicht verfügbar -- gilt dann nur für diese Sitzung.
    }
  };

  const [sprites, setSprites] = useState<Sprites | null>(null);
  const [spriteError, setSpriteError] = useState<string | null>(null);
  useEffect(() => {
    loadSprites().then(setSprites, (error: Error) => setSpriteError(error.message));
  }, []);

  const controllerRef = useRef<MapController>(null);
  const [colorMode, setColorMode] = useState<ColorMode>('beziehung');
  const [showGrid, setShowGrid] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 900);
  const [selected, setSelected] = useState<number | null>(null);
  const [highlight, setHighlight] = useState<Highlight>(null);
  const [view, setView] = useState({ x: 0, y: 0, zoom: 1 });
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);

  // Auswahl/Hervorhebung gehören zu einer Welt.
  useEffect(() => {
    setSelected(null);
    setHighlight(null);
  }, [activeWorld]);

  const highlightSet = useMemo(() => {
    const result = new Set<number>();
    if (!map || !highlight) return result;
    map.villages.forEach((village, i) => {
      const playerIndex = village[3];
      if (playerIndex < 0) return;
      if (highlight.kind === 'player' ? playerIndex === highlight.index : map.players[playerIndex]![2] === highlight.index) {
        result.add(i);
      }
    });
    return result;
  }, [map, highlight]);

  const searchResults = useMemo(() => {
    const query = deferredSearch.trim().toLowerCase();
    if (!map || query.length < 2) return null;
    const coordinates = parseCoordinates(query);
    const players: number[] = [];
    for (let i = 0; i < map.players.length && players.length < SEARCH_LIMIT; i++) {
      if (map.players[i]![1].toLowerCase().includes(query)) players.push(i);
    }
    const tribes: number[] = [];
    for (let i = 0; i < map.tribes.length && tribes.length < SEARCH_LIMIT; i++) {
      const [, tag, name] = map.tribes[i]!;
      if (tag.toLowerCase().includes(query) || name.toLowerCase().includes(query)) tribes.push(i);
    }
    const villages: number[] = [];
    for (let i = 0; i < map.villages.length && villages.length < SEARCH_LIMIT; i++) {
      if (map.villages[i]![5].toLowerCase().includes(query)) villages.push(i);
    }
    return { coordinates, players, tribes, villages };
  }, [map, deferredSearch]);

  const villageByCoord = useMemo(() => {
    const lookup = new Map<number, number>();
    map?.villages.forEach((village, i) => lookup.set(village[0] + village[1] * 10_000, i));
    return lookup;
  }, [map]);

  const nobleList = useMemo(() => {
    if (!map || !planDetail) return [];
    const plannedBySource = new Map<number, number>();
    for (const [, sx, sy, , , count] of planDetail.plannedNobles) {
      plannedBySource.set(sx + sy * 10_000, (plannedBySource.get(sx + sy * 10_000) ?? 0) + count);
    }
    return planDetail.nobles
      .map(([x, y, count]) => {
        const village = map.villages[villageByCoord.get(x + y * 10_000) ?? -1];
        const owner = village && village[3] >= 0 ? map.players[village[3]]![1] : null;
        return { x, y, count, planned: plannedBySource.get(x + y * 10_000) ?? 0, name: village ? village[5] : 'Unbekanntes Dorf', owner };
      })
      .sort((a, b) => b.count - a.count || a.y - b.y || a.x - b.x);
  }, [map, planDetail, villageByCoord]);

  // Zieldörfer mit AG-Bedarf: alle Ziele echter Angriffe (Fake-Typen ausgenommen) samt bisher verplanter AGs (Summe je Ziel);
  // ab NOBLES_PER_TARGET gilt der AG-Zug als komplett, sonst fehlt die Differenz.
  const plannedTargetList = useMemo(() => {
    const byTarget = new Map<number, { x: number; y: number; count: number }>();
    const touch = (x: number, y: number) => {
      const key = x + y * 10_000;
      let entry = byTarget.get(key);
      if (!entry) {
        entry = { x, y, count: 0 };
        byTarget.set(key, entry);
      }
      return entry;
    };
    for (const [, , tx, ty, , typeIndex] of planDetail?.commands ?? []) {
      if (!planDetail?.types[typeIndex]?.isFake) touch(tx, ty);
    }
    for (const [, , , tx, ty, count] of planDetail?.plannedNobles ?? []) touch(tx, ty).count += count;
    return [...byTarget.values()];
  }, [planDetail]);
  const completeTargetCount = plannedTargetList.filter((t) => t.count >= NOBLES_PER_TARGET).length;
  const openTargetCount = plannedTargetList.length - completeTargetCount;
  const missingNobles = plannedTargetList.reduce((sum, t) => sum + Math.max(0, NOBLES_PER_TARGET - t.count), 0);
  const mapPlannedTargets = useMemo(() => (showNobles ? plannedTargetList : []), [showNobles, plannedTargetList]);

  const mapNobles = useMemo(() => (showNobles && planDetail ? planDetail.nobles.map(([x, y, count]) => ({ x, y, count })) : []), [showNobles, planDetail]);

  // AG-Planung: gewählter Inc (Off) auf das markierte Zieldorf, Abstand hinter der Off, berechnete Kandidaten.
  const [selectedInc, setSelectedInc] = useState<number | null>(null);
  const [gapMs, setGapMs] = useState(1);
  useEffect(() => {
    setSelectedInc(null);
  }, [selected, planId]);
  const candidatesQuery = useNobleCandidates(activeWorld || undefined, planId, selectedInc, gapMs);
  const candidates = selectedInc !== null ? (candidatesQuery.data ?? null) : null;

  const mapMarkers = useMemo<MapMarker[]>(
    () => (candidates ? candidates.candidates.filter((c) => c.status === 'ok').map((c) => ({ x: c.x, y: c.y, color: '#4ade80' })) : []),
    [candidates],
  );

  // Verplante AGs als gestrichelte goldene Pfeile (zusammen mit den Plaketten ein-/ausblendbar).
  const mapArrows = useMemo<MapArrow[]>(() => {
    if (!showNobles || !planDetail) return arrows;
    const planned = planDetail.plannedNobles.map(([, sx, sy, tx, ty]) => ({ sx, sy, tx, ty, color: '#f2c94c', dashed: true }));
    return [...arrows, ...planned];
  }, [arrows, planDetail, showNobles]);

  const focusNoble = (x: number, y: number) => {
    const index = villageByCoord.get(x + y * 10_000);
    if (index !== undefined) setSelected(index);
    controllerRef.current?.centerOn(x, y, 1);
  };

  const goToVillage = (index: number) => {
    const village = map!.villages[index]!;
    setSelected(index);
    controllerRef.current?.centerOn(village[0], village[1], 1);
  };

  const focusGroup = (kind: 'player' | 'tribe', index: number) => {
    if (!map) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    map.villages.forEach(([x, y, , playerIndex]) => {
      if (playerIndex < 0) return;
      const match = kind === 'player' ? playerIndex === index : map.players[playerIndex]![2] === index;
      if (!match) return;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    });
    setHighlight({ kind, index });
    if (Number.isFinite(minX)) controllerRef.current?.fitRect(minX, minY, maxX, maxY);
  };

  const fitAllVillages = () => {
    if (!map || map.villages.length === 0) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of map.villages) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    controllerRef.current?.fitRect(minX, minY, maxX, maxY);
  };

  const selectedVillage = map && selected !== null ? map.villages[selected] : null;
  const selectedPlannedHere = useMemo(() => {
    if (!selectedVillage) return null;
    return plannedTargetList.find((t) => t.x === selectedVillage[0] && t.y === selectedVillage[1])?.count ?? null;
  }, [plannedTargetList, selectedVillage]);
  const selectedNobles = useMemo(() => {
    if (!planDetail || !selectedVillage) return 0;
    return planDetail.nobles.find((n) => n[0] === selectedVillage[0] && n[1] === selectedVillage[1])?.[2] ?? 0;
  }, [planDetail, selectedVillage]);
  // Befehle, die von dem gewählten Dorf ausgehen (Incs AUF das Dorf stehen im AG-Planungsbereich).
  const selectedCommands = useMemo(() => {
    if (!planDetail || !selectedVillage) return [];
    const [vx, vy] = selectedVillage;
    return planDetail.commands.filter((c) => c[0] === vx && c[1] === vy);
  }, [planDetail, selectedVillage]);

  const selectedIncs = useMemo<IncRow[]>(() => {
    if (!planDetail || !selectedVillage) return [];
    const [vx, vy] = selectedVillage;
    return planDetail.commands
      .filter((c) => c[2] === vx && c[3] === vy)
      .map((c) => {
        const type = planDetail.types[c[5]];
        return {
          id: c[7],
          sourceX: c[0],
          sourceY: c[1],
          arrivalMs: c[4],
          unit: c[6],
          typeLabel: type?.label ?? '?',
          color: type ? (effectiveColors[type.key] ?? type.color) : '#999',
        };
      })
      .sort((a, b) => a.arrivalMs - b.arrivalMs);
  }, [planDetail, selectedVillage, effectiveColors]);
  const selectedPlayer = map && selectedVillage && selectedVillage[3] >= 0 ? map.players[selectedVillage[3]] : null;
  const selectedTribe = map && selectedPlayer && selectedPlayer[2] >= 0 ? map.tribes[selectedPlayer[2]] : null;

  if (needsWorldChoice && !worldKey) {
    return (
      <section className="card card-narrow" style={{ margin: '40px auto' }}>
        <h1>AG Planer</h1>
        <label className="field">
          <span>Welt</span>
          <select value={worldKey} onChange={(event) => setSearchParams({ welt: event.target.value }, { replace: true })}>
            <option value="" disabled>
              Welt wählen …
            </option>
            {(worlds.data?.worlds ?? []).map((world) => (
              <option key={world.key} value={world.key}>
                {world.name}
              </option>
            ))}
          </select>
        </label>
      </section>
    );
  }

  const terrainStatus = map?.terrain;
  const loadingTerrain = terrainStatus?.status === 'loading' || terrainStatus?.status === 'missing';

  return (
    <div className="ag-planer">
      {!map || !sprites ? (
        <div className="ag-map ag-map-state">
          {mapQuery.isError ? (
            <div className="flash flash-error">{mapQuery.error.message}</div>
          ) : spriteError ? (
            <div className="flash flash-error">{spriteError}</div>
          ) : (
            <Spinner label="Karte wird geladen …" />
          )}
        </div>
      ) : (
        <MapCanvas
          ref={controllerRef}
          map={map}
          terrain={terrainQuery.data ?? null}
          sprites={sprites}
          colorMode={colorMode}
          showGrid={showGrid}
          highlight={highlightSet}
          arrows={mapArrows}
          nobles={mapNobles}
          markers={mapMarkers}
          plannedTargets={mapPlannedTargets}
          selectedVillage={selected}
          onSelectVillage={setSelected}
          onViewChange={setView}
        />
      )}

      {map && (
        <div className="ag-map-overlay">
          <span>
            {Math.floor(view.x)}|{Math.floor(view.y)} · K{continentOf(Math.floor(view.x), Math.floor(view.y))} · {Math.round(view.zoom * 100)}%
          </span>
          {loadingTerrain && (
            <span className="ag-map-notice">
              Gelände wird geladen
              {terrainStatus?.status === 'loading' && terrainStatus.total > 1
                ? ` (${Math.round((terrainStatus.done / terrainStatus.total) * 100)} %)`
                : ' …'}
            </span>
          )}
          {terrainStatus?.status === 'error' && (
            <span className="ag-map-notice ag-map-notice-error">
              Gelände nicht verfügbar: {terrainStatus.message} (Karte ohne Gelände)
            </span>
          )}
        </div>
      )}

      <button
        type="button"
        className="btn btn-sm ag-sidebar-toggle"
        onClick={() => setSidebarOpen((open) => !open)}
        aria-expanded={sidebarOpen}
      >
        {sidebarOpen ? 'Menü ausblenden ›' : '‹ Menü'}
      </button>

      {sidebarOpen && (
        <aside className="ag-sidebar">
          <div className="ag-side-section">
            <h2>AG Planer</h2>
            {map ? (
              <p className="muted">
                {map.world.name} · {formatPoints(map.villages.length)} Dörfer
                {map.syncedAt ? ` · Stand ${new Date(map.syncedAt).toLocaleString('de-DE')}` : ''}
              </p>
            ) : (
              <p className="muted">Karte wird geladen …</p>
            )}
          </div>

          {map && (
            <>
              <PlanPanel
                welt={activeWorld}
                plans={plansQuery.data?.plans ?? []}
                plansLoading={plansQuery.isPending}
                planId={planId}
                onSelectPlan={selectPlan}
                detail={planDetail}
                detailLoading={planId !== null && planQuery.isPending}
                hiddenTypes={hiddenTypes}
                onToggleType={(key) => {
                  const next = new Set(hiddenTypes);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  updateHiddenTypes(next);
                }}
                onSetAllTypes={(visible) => updateHiddenTypes(visible ? new Set() : new Set(planDetail?.types.map((t) => t.key) ?? []))}
                colors={effectiveColors}
                onChangeColor={changeColor}
                showNobles={showNobles}
                onToggleNobles={toggleNobles}
                nobleList={nobleList}
                onFocusNoble={focusNoble}
                completeTargetCount={completeTargetCount}
                openTargetCount={openTargetCount}
                missingNobles={missingNobles}
              />

              <div className="ag-side-section">
                <h3>Suche</h3>
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Spieler, Stamm, Dorf oder 567|521"
                  aria-label="Karte durchsuchen"
                />
                {searchResults && (
                  <ul className="ag-results">
                    {searchResults.coordinates && (
                      <li>
                        <button
                          type="button"
                          onClick={() => {
                            const { x, y } = searchResults.coordinates!;
                            const index = villageByCoord.get(x + y * 10_000);
                            if (index !== undefined) setSelected(index);
                            controllerRef.current?.centerOn(x, y, 1);
                          }}
                        >
                          Zu {searchResults.coordinates.x}|{searchResults.coordinates.y} springen
                        </button>
                      </li>
                    )}
                    {searchResults.players.map((i) => (
                      <li key={`p${i}`}>
                        <button type="button" onClick={() => focusGroup('player', i)}>
                          Spieler {map.players[i]![1]}
                        </button>
                      </li>
                    ))}
                    {searchResults.tribes.map((i) => (
                      <li key={`t${i}`}>
                        <button type="button" onClick={() => focusGroup('tribe', i)}>
                          Stamm {map.tribes[i]![2]} [{map.tribes[i]![1]}]
                        </button>
                      </li>
                    ))}
                    {searchResults.villages.map((i) => (
                      <li key={`v${i}`}>
                        <button type="button" onClick={() => goToVillage(i)}>
                          {map.villages[i]![5]} ({map.villages[i]![0]}|{map.villages[i]![1]})
                        </button>
                      </li>
                    ))}
                    {!searchResults.coordinates &&
                      searchResults.players.length + searchResults.tribes.length + searchResults.villages.length === 0 && (
                        <li className="muted">Keine Treffer.</li>
                      )}
                  </ul>
                )}
                {highlight && (
                  <button type="button" className="btn btn-sm" onClick={() => setHighlight(null)}>
                    Hervorhebung entfernen (
                    {highlight.kind === 'player' ? map.players[highlight.index]![1] : map.tribes[highlight.index]![1]})
                  </button>
                )}
              </div>

              <div className="ag-side-section">
                <h3>Ansicht</h3>
                <label className="checkbox-field">
                  <input type="radio" name="ag-color" checked={colorMode === 'beziehung'} onChange={() => setColorMode('beziehung')} />
                  <span>Farben nach Beziehung</span>
                </label>
                <label className="checkbox-field">
                  <input type="radio" name="ag-color" checked={colorMode === 'staemme'} onChange={() => setColorMode('staemme')} />
                  <span>Farben nach Stämmen</span>
                </label>
                <label className="checkbox-field">
                  <input type="checkbox" checked={showGrid} onChange={(event) => setShowGrid(event.target.checked)} />
                  <span>Raster und Koordinaten</span>
                </label>
                <div className="button-row">
                  <button type="button" className="btn btn-sm" onClick={() => controllerRef.current?.zoomBy(1.4)} aria-label="Hineinzoomen">
                    +
                  </button>
                  <button type="button" className="btn btn-sm" onClick={() => controllerRef.current?.zoomBy(1 / 1.4)} aria-label="Herauszoomen">
                    −
                  </button>
                  <button type="button" className="btn btn-sm" onClick={fitAllVillages}>
                    Alle Dörfer
                  </button>
                </div>
              </div>

              <div className="ag-side-section">
                <h3>Legende</h3>
                {colorMode === 'beziehung' ? (
                  <ul className="ag-legend">
                    <li>
                      <i style={{ background: COLORS.self }} /> Eigene Spielfigur
                    </li>
                    <li>
                      <i style={{ background: COLORS.ownTribe }} /> Eigener Stamm (verwaltet)
                    </li>
                    <li>
                      <i style={{ background: COLORS.other }} /> Andere
                    </li>
                    <li>
                      <i style={{ background: COLORS.barbarian }} /> Barbaren
                    </li>
                  </ul>
                ) : (
                  <p className="muted">Jeder Stamm hat eine eigene Farbe; Spieler ohne Stamm sind braun.</p>
                )}
              </div>

              {selectedVillage && (
                <div className="ag-side-section">
                  <h3>Auswahl</h3>
                  <p>
                    <strong>{selectedVillage[5]}</strong> ({selectedVillage[0]}|{selectedVillage[1]}) K
                    {continentOf(selectedVillage[0], selectedVillage[1])}
                  </p>
                  <p className="muted">
                    Besitzer: {selectedPlayer ? selectedPlayer[1] : 'Barbarendorf'}
                    {selectedTribe ? ` · ${selectedTribe[2]} [${selectedTribe[1]}]` : ''}
                    <br />
                    Punkte: {formatPoints(selectedVillage[2])}
                    {selectedVillage[4] === 1 ? ' · Bonusdorf' : ''}
                    {selectedPlannedHere !== null && (
                      <>
                        <br />
                        AGs verplant: {selectedPlannedHere} von {NOBLES_PER_TARGET}
                        {selectedPlannedHere >= NOBLES_PER_TARGET ? ' ✓ komplett' : ` · ${NOBLES_PER_TARGET - selectedPlannedHere} fehlen`}
                      </>
                    )}
                    {selectedNobles > 0 && (
                      <>
                        <br />
                        AGs: {selectedNobles} (ungeplant)
                      </>
                    )}
                  </p>
                  {selectedCommands.length > 0 && planDetail && (
                    <>
                      <h3 style={{ margin: '4px 0 0' }}>Befehle von diesem Dorf</h3>
                      <ul className="ag-commands">
                        {selectedCommands.slice(0, 12).map((c, i) => {
                          const type = planDetail.types[c[5]];
                          return (
                            <li key={i}>
                              <i style={{ background: type ? (effectiveColors[type.key] ?? type.color) : '#999' }} />
                              → {c[2]}|{c[3]} · {type?.label ?? '?'}
                              <br />
                              <span className="muted">Ankunft {formatDateTime(c[4])}</span>
                            </li>
                          );
                        })}
                        {selectedCommands.length > 12 && <li className="muted">… und {selectedCommands.length - 12} weitere</li>}
                      </ul>
                    </>
                  )}
                  <div className="button-row">
                    {selectedPlayer && (
                      <button type="button" className="btn btn-sm" onClick={() => focusGroup('player', selectedVillage[3])}>
                        Spieler hervorheben
                      </button>
                    )}
                    {selectedTribe && selectedPlayer && (
                      <button type="button" className="btn btn-sm" onClick={() => focusGroup('tribe', selectedPlayer[2])}>
                        Stamm hervorheben
                      </button>
                    )}
                  </div>
                </div>
              )}

              {selectedVillage && planDetail && planId !== null && (
                <NoblePlanningPanel
                  welt={activeWorld}
                  planId={planId}
                  detail={planDetail}
                  village={{ x: selectedVillage[0], y: selectedVillage[1] }}
                  incs={selectedIncs}
                  selectedInc={selectedInc}
                  onSelectInc={setSelectedInc}
                  gapMs={gapMs}
                  onGapChange={setGapMs}
                  candidates={candidates}
                  candidatesLoading={selectedInc !== null && candidatesQuery.isFetching}
                  candidatesError={selectedInc !== null && candidatesQuery.isError ? candidatesQuery.error.message : null}
                />
              )}
            </>
          )}
        </aside>
      )}
    </div>
  );
}
